import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { LoadingSpinner } from "@/components/loading-spinner";
import { Separator } from "@/components/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { CalendarDates } from "@/custom-types";
import { useJiraMetricsProjects, useJiraMetricsSummary, useJiraMetricsSyncRuns } from "@/http/api-hooks";
import { useUrlParams } from "@/http/use-url-params";
import { formatDateToYYYYMMDD } from "@/lib/utils";
import { previousSunday, subWeeks } from "date-fns";
import { useState } from "react";
import { AgingWipTable } from "./aging-wip-table";
import { ApprovedToDoneTrendChart } from "./approved-to-done-trend-chart";
import { CfdChart } from "./cfd-chart";
import { CycleTimeHistogramChart } from "./cycle-time-histogram-chart";
import { CycleTimePercentileTrendChart } from "./cycle-time-percentile-trend-chart";
import { CycleTimeTrendChart } from "./cycle-time-trend-chart";
import { DoneDateVsCycleScatterChart } from "./done-date-vs-cycle-scatter-chart";
import { formatDuration } from "./formatters";
import { RecentSyncRunsTable } from "./recent-sync-runs-table";
import { ReviewVsCycleScatterChart } from "./review-vs-cycle-scatter-chart";
import { SlaFunnelChart } from "./sla-funnel-chart";
import { StageCompositionChart } from "./stage-composition-chart";
import { ThroughputChart } from "./throughput-chart";
import { TicketDetailTable } from "./ticket-detail-table";

function defaultDateRange() {
  const today = new Date();
  const lastSunday = today.getDay() === 0 ? today : previousSunday(today);
  const from = subWeeks(lastSunday, 16);
  from.setDate(from.getDate() + 1); // Monday
  return { from, to: lastSunday };
}

function MetricCard({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

export default function JiraMetricsPage() {
  const { paramState, updateDateRange, updateParam } = useUrlParams({
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: defaultDateRange().from,
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: defaultDateRange().to,
    },
    projects: {
      name: "projects",
      type: "string",
      defaultValue: "UA",
    },
    window: {
      name: "window",
      type: "enum" as const,
      validValues: ["week", "2week", "4week"] as const,
      defaultValue: "week",
    },
  });

  const currentDate: CalendarDates = {
    from: paramState.fromDate as Date,
    to: paramState.toDate as Date,
  };

  const projectsParam = paramState.projects as string;
  const isAllTeams = projectsParam === "all";
  const selectedProjects = isAllTeams ? undefined : projectsParam?.replace(/\./g, ",") || undefined;
  const selectedProjectKeys = isAllTeams ? [] : (projectsParam?.split(".").filter(Boolean) ?? []);
  const { data: availableProjects } = useJiraMetricsProjects();
  const sortedProjects = availableProjects ? [...availableProjects].sort() : [];

  const groupBy = (paramState.window as string) || "week";

  const [selectedTicketKey, setSelectedTicketKey] = useState("");

  const from = currentDate?.from ? formatDateToYYYYMMDD(currentDate.from) : undefined;
  const to = currentDate?.to ? formatDateToYYYYMMDD(currentDate.to) : undefined;
  const shouldFetch = Boolean(from && to);

  const {
    data: summaryResponse,
    isLoading: summaryLoading,
    error: summaryError,
  } = useJiraMetricsSummary(
    {
      from,
      to,
      group_by: groupBy as "week" | "2week" | "4week",
      done_only: true,
      projects: selectedProjects,
    },
    shouldFetch,
  );

  const { data: syncRunsResponse, isLoading: syncRunsLoading } = useJiraMetricsSyncRuns(10);

  const summary = summaryResponse?.data;
  const kpi = summary?.kpi;
  const syncRuns = syncRunsResponse?.data ?? [];

  const openTicketDetailsForTicketKey = (ticketKey: string) => {
    if (!ticketKey) {
      return;
    }

    setSelectedTicketKey(ticketKey);
    document.getElementById("ticket-detail-table")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Card className="mb-4 h-fit relative z-0">
        <CardContent className="flex flex-wrap items-center gap-4 py-3">
          <CalendarDateRangePicker
            date={currentDate}
            presetVariant="weeks"
            setDate={selectedDate => {
              updateDateRange(selectedDate, "fromDate", "toDate");
            }}
          />
          <Separator orientation="vertical" className="h-8" />
          <ToggleGroup
            type="multiple"
            variant="outline"
            size="sm"
            value={selectedProjectKeys}
            onValueChange={(value: string[]) => {
              updateParam("projects", value.length > 0 ? value.sort().join(".") : "all");
            }}
          >
            <ToggleGroupItem
              value="__all__"
              className="min-w-fit px-3"
              data-state={isAllTeams ? "on" : "off"}
              onClick={e => {
                e.preventDefault();
                updateParam("projects", "all");
              }}
            >
              All Teams
            </ToggleGroupItem>
            {sortedProjects.map(key => (
              <ToggleGroupItem key={key} value={key} className="min-w-fit px-3">
                {key}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Separator orientation="vertical" className="h-8" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Window</span>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={groupBy}
              onValueChange={value => {
                if (value) updateParam("window", value);
              }}
            >
              <ToggleGroupItem value="week" className="min-w-fit px-3">
                1w
              </ToggleGroupItem>
              <ToggleGroupItem value="2week" className="min-w-fit px-3">
                2w
              </ToggleGroupItem>
              <ToggleGroupItem value="4week" className="min-w-fit px-3">
                4w
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardContent>
      </Card>

      <Separator className="my-6" />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>How to Read This Dashboard</CardTitle>
          <CardDescription>
            Cycle time charts only use tickets that have moved to Done in the selected date range. Aging WIP focuses on
            open tickets that entered In Progress. Experiment tickets are excluded from all metrics.
          </CardDescription>
        </CardHeader>
      </Card>

      {summaryError && <div className="text-red-600 mb-4">Failed to load summary data.</div>}
      {summaryLoading && <LoadingSpinner />}

      {kpi && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 mb-8">
          <MetricCard
            title="Average Cycle Time"
            value={formatDuration(kpi.avg_cycle_seconds)}
            subtitle="Done tickets"
          />
          <MetricCard
            title="Median Cycle Time"
            value={formatDuration(kpi.median_cycle_seconds)}
            subtitle="Done tickets"
          />
          <MetricCard
            title="Avg Review Time"
            value={formatDuration(kpi.avg_review_seconds)}
            subtitle="Time in review"
          />
          <MetricCard
            title="Avg In Progress Time"
            value={formatDuration(kpi.avg_in_progress_seconds)}
            subtitle="Time in progress"
          />
        </div>
      )}

      {summary && (
        <div className="grid gap-4 xl:grid-cols-2 mb-8">
          <CycleTimeTrendChart data={summary.cycle_trend ?? []} />
          <ThroughputChart data={summary.throughput_trend ?? []} />
          <StageCompositionChart data={summary.stage_composition ?? []} />
          <CycleTimeHistogramChart data={summary.histogram ?? []} />
          <ApprovedToDoneTrendChart data={summary.stage_composition ?? []} />
          <CycleTimePercentileTrendChart data={summary.cycle_time_percentile_trend ?? []} />
          <ReviewVsCycleScatterChart data={summary.scatter ?? []} />
          <DoneDateVsCycleScatterChart
            data={summary.control_chart ?? []}
            onTicketSelect={openTicketDetailsForTicketKey}
          />
          <CfdChart data={summary.cfd ?? []} />
          <SlaFunnelChart data={summary.sla_funnel ?? []} />
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2 mb-8">
        <AgingWipTable rows={summary?.aging_wip || []} />
        <RecentSyncRunsTable rows={syncRuns} isLoading={syncRunsLoading} />
      </div>

      <TicketDetailTable from={from} to={to} projects={selectedProjects} selectedTicketKey={selectedTicketKey} />
    </>
  );
}
